import React, { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * MasterGuard wraps every Masters route. It requires the user to enter the
 * Masters access password (`CPMS@2004`) once per browser tab/session before
 * the children render. The unlock state is stored in sessionStorage so it
 * resets when the tab is closed.
 */
const MASTERS_PASSWORD = "CPMS@2004";
const STORAGE_KEY = "cpms_masters_unlocked";

export default function MasterGuard({ children }) {
  const [unlocked, setUnlocked] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY) === "1"
  );
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    setPwd("");
  }, [unlocked]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (pwd === MASTERS_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, "1");
      setUnlocked(true);
      toast.success("Masters unlocked for this session");
    } else {
      setError("Incorrect password");
      setPwd("");
    }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="master-guard">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-[#1D2951]/10 flex items-center justify-center">
            <Lock size={18} className="text-[#1D2951]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Restricted Section</div>
            <h2 className="text-lg font-semibold text-slate-900">Masters Access</h2>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Enter the Masters password to manage company reference data. Stays unlocked until you close this tab.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <input
            autoFocus
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Masters password"
            data-testid="master-guard-password-input"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
          />
          {error && <div className="text-xs text-rose-600 font-semibold" data-testid="master-guard-error">{error}</div>}
          <button
            type="submit"
            data-testid="master-guard-submit"
            className="w-full bg-[#1D2951] hover:bg-[#2B3A67] text-white font-semibold text-sm px-4 py-2 rounded-md"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
}
